import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'

import CouponService from '@spa/services/api/coupon'
import Coupon, { BrandCoupon, CommonCoupon } from '../models/coupon'
import CouponCollection from '../models/coupon-collection'
import PagingSetting, { defaultPagingSetting } from '../models/paging-setting'

export default class CouponInteractor {

  constructor(
    private service: CouponService,
    private setting: PagingSetting = defaultPagingSetting
  ) {}

  async loadCommonCoupons(
    collection: CouponCollection
  ): Promise<Either<Error, CouponCollection>> {
    // 既にロードを最後まで終えている場合は何もしない.
    if (collection.commonLoadDone) {
      return new Right(collection)
    }

    // API呼び出しで次のクーポンを取得.
    const result = await this.loadNextCoupons(null, collection)

    return result
  }

  async loadBrandCoupons(
    brandEnglishName: string,
    collection: CouponCollection
  ): Promise<Either<Error, CouponCollection>> {
    // 既にロードを最後まで終えている場合は何もしない.
    if (collection.brandLoadDone(brandEnglishName)) {
      return new Right(collection)
    }

    // API呼び出しで次のクーポンを取得.
    const result = await this.loadNextCoupons(brandEnglishName, collection)

    return result
  }

  // 次ページ表示用にサーバーからクーポンを一定数取得.
  // - スキャン用のAPI呼び出しでクーポンを取得.
  // - APIからの結果は, 表示可能なクーポンのみに, この関数内でフィルタリングする.
  //     - 表示可能な条件: (クーポンコードが設定されていない | クーポンが使用済み)
  // - 一定数取得までループして何度もAPIを呼ぶ. スキャンが終端まで行ったら終わり.
  //
  // 検討内容: https://github.com/my-color/front/pull/4960#discussion_r585532091
  //
  private async loadNextCoupons(
    brandEnglishName: string | null,
    collection: CouponCollection
  ): Promise<Either<Error, CouponCollection>> {
    // 読み込み済みのクーポンを取得.
    const loaded = brandEnglishName
       ? collection.brandCoupons(brandEnglishName)
       : collection.commonCoupons

    // ロードの開始地点は, 既に読み込みしている地点の次の地点から.
    let start = this.getStartIndex(loaded)

    // limit個読み取るまでループ.
    const limit = this.setting.perPage
    const coupons: Coupon[] = []
    while (true) {
      // APIから読み込み.
      const result = brandEnglishName
        ? await this.service.brandListWithRange(brandEnglishName, start, limit)
        : await this.service.commonListWithRange(start, limit)

      // エラーが出てしまったらその時点で終了.
      if (result.isLeft()) {
        return new Left(result.value)
      }

      // 終了フラグ.
      // 読み取った個数が要求した個数より少なかった場合, ロードは最後まで終了したのでフラグを立てておく.
      let loadDone = false
      if (result.value.length < limit) {
        loadDone = true
        if (brandEnglishName) {
          // ブランド名が指定してあればそれ.
          collection.setBrandLoaded(brandEnglishName)
        } else {
          // 指定がなければ共通.
          collection.setCommonLoaded()
        }
      }

      // APIの戻り値をクーポン一覧で使うCouponクラスへ変換.
      // また, APIからの戻り値には一覧に表示させないものも含まれているので, フィルタリングも行う.
      result.value.forEach((c, index) => {
        // Couponクラスに変換. indexはstartからの絶対値に変換.
        const coupon: Coupon = brandEnglishName
          ? new BrandCoupon(brandEnglishName, c, start + index)
          : new CommonCoupon(c, start + index)

        if (coupons.length < limit) {
          // 表示可能なクーポンのみ戻り値に追加する.
          if (!c.hasCouponCode) {
            // クーポンコードがないものは表示OK.
            coupons.push(coupon)
          }
        }
      })

      // limitまで到達したらループ終了.
      if (coupons.length >= limit) {
        break
      }

      // ロードが最後まで完了した場合もループ終了.
      if (loadDone) {
        break
      }

      // ループ続行の場合はstart地点を更新する.
      start = start + result.value.length
    }

    // 新たにロードしたクーポンを元の集合に追加.
    collection.addCoupons(coupons)

    return new Right(collection)
  }

  // 次のスキャン開始地点を取得.
  private getStartIndex(coupons: Coupon[]): number {
    // 何も読み込んでいなかったら0から.
    if (coupons.length === 0) {
      return 0
    }

    // 既に読み込んでいるものがあれば, 最後の要素のインデックス+1から始める.
    const lastIndex = coupons[coupons.length - 1].index

    return lastIndex + 1
  }
}
